<script lang="ts" setup>
import { useStoreConfig } from '~/store/storeConfig'
import getWidgetTextTranslation from '~/utils/getWidgetTextTranslation'
import getDiscount from '~/utils/getDiscount'
import getWidgetTranslatedMediaUrl from '~/utils/getWidgetTranslatedMediaUrl'

import type { ProductResponseInterface } from '~/types/market/productBackendTypes'
import type { TranslatableString } from '~/types/widgets/widgetTypes'
import type { Product } from '~/types/searchTypes'
import buildCategoryUrl from '~/utils/filters/buildCategoryUrl'
import type { RouteLocationRaw } from 'vue-router'
import parseProductSlug from '~/utils/parseProductSlug'
import buildProductUrl from '@/utils/routing/buildProductUrl'
import { fetchRetryParameters, requestHostHeaders } from '~/composables/constants/api'
import type { RequestHostHeadersType } from '~/composables/constants/api'

const props = defineProps<{
  slug: TranslatableString
  backgroundMobile: string
  backgroundDesktop: string
  productSlug: TranslatableString
  backgroundColor?: string
  title: TranslatableString
  openLinksInNewTab?: boolean
}>()
const storeConfig = useStoreConfig()

const product = ref<Partial<ProductResponseInterface>>()

const { isMobile } = useStoreConfig()
const { t } = useLang()
const localeSetting = useState<string>('locale.setting')

const buttonSlug = ref<RouteLocationRaw | TranslatableString>(props.slug)
const productsSlug = ref()

watch(
  localeSetting,
  async (locale) => {
    const { productSlugByLocale, slug, variantNumber } = parseProductSlug(props.productSlug)

    try {
      const langId = storeConfig.getSite?.languages?.find((lang) => lang.iso_code.toLowerCase() === locale)?.id

      const response = await $fetch<ProductResponseInterface>(`/api/market/product`, {
        method: 'POST',
        body: {
          slug,
          langId,
        },
        ...fetchRetryParameters,
        headers: useRequestHeaders<RequestHostHeadersType>(requestHostHeaders),
      })

      if (response && response.id !== null) {
        product.value = response

        if (variantNumber) {
          const variantData = response.other_items.find((variant) => variant.id === Number(variantNumber))

          product.value = variantData
            ? {
                ...variantData,
                medias: [
                  {
                    width: 0,
                    height: 0,
                    image: variantData.media.reverse()[0],
                  },
                ],
              }
            : response
        }

        buttonSlug.value = getWidgetTextTranslation(props.slug)
        productsSlug.value = productSlugByLocale
      } else {
        const fallbackItem = await $fetch<Product>(`/api/market/productFallback`, {
          method: 'POST',
          body: {
            locale,
            langId,
          },
          headers: useRequestHeaders<RequestHostHeadersType>(requestHostHeaders),
        })

        if (fallbackItem) {
          product.value = {
            id: fallbackItem.id,
            medias: [{ image: fallbackItem.image, width: 0, height: 0 }],
            default_values: { manufacturer: fallbackItem.manufacturer } as ProductResponseInterface['default_values'],
            name: fallbackItem.name,
            old_price: fallbackItem.old_price,
            current_price: fallbackItem.current_price,
          }

          buttonSlug.value = buildCategoryUrl(fallbackItem.quote_slug)
          productsSlug.value = buildProductUrl(fallbackItem.quote_slug)
        }
      }
    } catch (error) {}
  },
  {
    immediate: true,
    deep: true,
  }
)

const backgroundImage = computed(() => (isMobile ? props.backgroundMobile : props.backgroundDesktop))
const discount = computed(() => getDiscount(product.value?.current_price, product.value?.old_price))
</script>

<template>
  <WidgetLayout v-if="product?.id" :background-color="props.backgroundColor">
    <div
      class="product-banner flex w-full h-full flex-col justify-end sm:flex-row lg:min-h-[20rem] md:min-w-[30rem] flex-nowrap p-4 lg:p-8 gap-6"
      :style="{
        backgroundImage: `url(${getWidgetTranslatedMediaUrl(backgroundImage)})`,
      }">
      <div
        :class="{
          'flex flex-col flex-1 w-full justify-between gap-5 md:min-w-[18rem] lg:p-8': true,
        }">
        <h1 class="text-white" v-text="getWidgetTextTranslation(props.title)" />
        <NuxtLink :to="buttonSlug" class="!hidden sm:!flex btn-white max-w-max">
          <span v-text="t(isMobile ? 'common.choose' : 'common.choose-products')" />
          <i class="fa-solid fa-arrow-right" />
        </NuxtLink>
      </div>
      <div class="product-card">
        <NuxtLink v-if="isMobile" :to="productsSlug" class="absolute w-full h-full z-20 top-0 left-0" />
        <figure class="img-wrapper">
          <NuxtImg v-if="product?.medias?.[0]?.image" :src="product?.medias?.[0]?.image" height="330" />
          <PromoSticker :enabled="product.sticker_needed" :src="product.sticker" />
          <div class="absolute top-0 left-0">
            <Sticker v-if="discount">
              {{ discount }}
            </Sticker>
          </div>
        </figure>
        <div class="flex flex-col flex-1">
          <div class="flex flex-col flex-1 justify-between md:justify-start">
            <div>
              <span class="manufacturer text-text2 dark:text-dmText2 lg:font-medium">{{
                product?.default_values?.manufacturer
              }}</span>
              <div class="title text-text1 dark:text-dmText1 overflow-hidden lg:text-lg">{{ product.name }}</div>
            </div>

            <ProductCardPrice :old-price="product.old_price" :current-price="product.current_price" />
          </div>
          <NuxtLink v-if="!isMobile" class="btn-secondary" :to="productsSlug">
            {{ t('common.view') }}
          </NuxtLink>
        </div>
      </div>
      <NuxtLink :to="buttonSlug" class="flex sm:!hidden btn-white w-full">
        <span v-text="t(isMobile ? 'common.choose' : 'common.choose-products')" />
        <i class="fa-solid fa-arrow-right" />
      </NuxtLink>
    </div>
  </WidgetLayout>
</template>

<style lang="postcss" scoped>
:deep(.product-banner) {
  .product-card {
    @apply relative h-auto flex flex-row p-2 gap-2 bg-white dark:bg-dmGray1 border border-neutral6 dark:border-dmGray1 rounded-sm;
    @apply lg:max-w-full lg:flex-1 lg:self-auto lg:p-4;
  }
}

.product-card {
  .img-wrapper {
    @apply flex items-center justify-center flex-1 w-full relative;
    @apply min-w-[90px] max-w-[150px] lg:max-w-[330px];
    @apply min-h-[90px] max-h-[150px] lg:max-h-[330px];

    img {
      margin: 0 auto;
      object-fit: contain;
      max-width: 100%;
      max-height: 100%;
      width: 100%;
      height: 100%;
    }
  }
}

.product-banner {
  border-radius: theme('vars.borderRadiusLight');
  overflow: hidden;
  background-size: cover;
  background-position: right;
}

.product-card {
  .title {
    font-size: 1.125rem;
    overflow: hidden;
    text-overflow: ellipsis;
    display: -webkit-box;
    -webkit-line-clamp: 3;
    line-clamp: 3;
    -webkit-box-orient: vertical;
  }

  .price {
    justify-content: flex-end;
    color: theme('colors.black');

    .old-price + .current-price {
      margin-right: 0.625rem;
      color: theme('colors.brand.primary');
      line-height: 1;
    }

    .old-price {
      text-decoration: line-through;
    }

    .current-price {
      font-weight: 700;
    }
  }
}

.btn-white {
  align-items: baseline;

  i {
    margin-left: 1rem;
  }
}
</style>
